import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Plus, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

const RSVP_CLS = { yes: "bg-[#ff000022] text-[color:var(--accent)] border-[#ff000044]", no: "bg-[#ff3b5c20] text-[#ff3b5c] border-[#ff3b5c40]", maybe: "bg-[#ffc70020] text-[#ffc700] border-[#ffc70040]" };

export default function Events() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/events").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const rsvp = async (id, status) => { await api.post(`/events/${id}/rsvp`, { status }); load(); };
  const del = async (id) => { if (!window.confirm("Delete event?")) return; await api.delete(`/events/${id}`); load(); };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };
  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Communications</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Events</h1>
        </div>
        {canManage && <button onClick={() => setOpen(true)} data-testid="new-event" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}><Plus size={16} /> New event</button>}
      </div>
      <div className="space-y-4">
        {rows.map((e) => (
          <div key={e.id} data-testid={`ev-${e.id}`} className="card-surface p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold">{e.title}</h3>
                <div className="text-xs text-[color:var(--accent)] mt-1">{fmt(e.start_at)}{e.end_at && ` — ${fmt(e.end_at)}`}</div>
                {e.location && <div className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1"><MapPin size={11} /> {e.location}</div>}
                {e.description && <p className="text-slate-300 mt-2 text-sm">{e.description}</p>}
              </div>
              {canManage && <button onClick={() => del(e.id)} className="text-slate-500 hover:text-[#ff3b5c]"><Trash2 size={14} /></button>}
            </div>
            <div className="mt-3 flex items-center gap-2">
              {["yes", "maybe", "no"].map((s) => (
                <button key={s} onClick={() => rsvp(e.id, s)} data-testid={`ev-rsvp-${e.id}-${s}`} className={`px-3 py-1.5 rounded-lg text-xs border ${e.my_rsvp === s ? RSVP_CLS[s] : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                  {s === "yes" ? "✓ Going" : s === "maybe" ? "? Maybe" : "✕ Can't"}
                </button>
              ))}
              <div className="ml-auto text-xs text-slate-400">{e.counts.yes} going · {e.counts.maybe} maybe · {e.counts.no} out</div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="card-surface p-10 text-center text-slate-500"><Calendar size={24} className="inline mr-2" /> No events scheduled.</div>}
      </div>
      {open && <NewEvent onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewEvent({ onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", start_at: "", end_at: "", location: "" });
  const submit = async (e) => { e.preventDefault(); await api.post("/events", f); onDone(); toast.success("Event created"); };
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="ev-modal">
        <h2 className="text-xl font-bold mb-4">New event</h2>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Title *</div><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ev-title" className={inp} /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Location</div><input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="ev-location" className={inp} placeholder="e.g. Discord voice channel #general" /></label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label><div className="text-xs uppercase text-slate-500 mb-1">Start *</div><input type="datetime-local" required value={f.start_at} onChange={(e) => setF({ ...f, start_at: e.target.value })} data-testid="ev-start" className={inp} /></label>
          <label><div className="text-xs uppercase text-slate-500 mb-1">End</div><input type="datetime-local" value={f.end_at} onChange={(e) => setF({ ...f, end_at: e.target.value })} data-testid="ev-end" className={inp} /></label>
        </div>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Description</div><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ev-desc" className={inp + " resize-none"} /></label>
        <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button><button type="submit" data-testid="ev-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Create</button></div>
      </form>
    </div>
  );
}
